import type { PlayerResponse, RosterItemResponse } from '@on3/api';
import type { ITeamRosterProps } from '@on3/ui-lib/api/schema/custom-contracts';
import { Link } from '@on3/ui-lib/components/Link';
import { Toggle } from '@on3/ui-lib/components/Toggle/Toggle';
import { useAuth } from '@on3/ui-lib/contexts/AuthProvider';
import { useToggle } from '@on3/ui-lib/index';
import { databaseUrl } from '@on3/ui-lib/utils/api';
import {
  formatMillions,
  parseJerseySortValue,
} from '@on3/ui-lib/utils/formatters';
import { ordinalSuffixOf } from '@on3/ui-lib/utils/ordinalSuffixOf';
import { createUrl } from '@on3/ui-lib/utils/url';
import { InterestListFilters } from '@public/components/College/Interests/General/InterestListFilters';
import { InterestsNav } from '@public/components/College/Interests/Nav';
import { TeamRosterListHeader } from '@public/components/College/Roster/Header';
import { TeamRosterItem } from '@public/components/College/Roster/TeamRosterItem/TeamRosterItem';
import { IndustryComparisonListAlt } from '@public/components/Db/Rankings/IndustryComparison/IndustryComparisonListAlt';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';

import styles from './TeamRoster.module.scss';

enum ClassRank {
  None = 'None',
  Freshman = 'Freshman',
  rsFreshman = 'RedShirt Freshman',
  Sophomore = 'Sophomore',
  rsSophomore = 'RedShirt Sophomore',
  Junior = 'Junior',
  rsJunior = 'RedShirt Junior',
  Senior = 'Senior',
  rsSenior = 'RedShirt Senior',
}
const classRankValue = {
  [ClassRank.None]: 0,
  [ClassRank.Freshman]: 1,
  [ClassRank.rsFreshman]: 2,
  [ClassRank.Sophomore]: 3,
  [ClassRank.rsSophomore]: 4,
  [ClassRank.Junior]: 5,
  [ClassRank.rsJunior]: 6,
  [ClassRank.Senior]: 7,
  [ClassRank.rsSenior]: 8,
};

const getClassRank = (value?: string | null): ClassRank => {
  return (value as ClassRank) ?? ClassRank.None;
};

// TODO: Break out into seperate functions

const useSortList = (list?: RosterItemResponse[] | null) =>
  useCallback(
    (sortBy: keyof PlayerResponse | string, ascending: boolean) => {
      if (!list?.length) {
        return list;
      }

      const isJersey = sortBy === 'jerseyNumber';
      const isName = sortBy === 'lastName';
      const isVerified = sortBy === 'athleteVerified';
      const isClassRank = sortBy === 'classRank';
      const isAge = sortBy === 'age';
      const isPosition = sortBy === 'position';
      const isRating = sortBy === 'rating';
      const isNilValue = sortBy === 'nilValue';

      const sortedList = [...list]?.sort((a, b) => {
        let setA = null;
        let setB = null;
        let secondarySortA = null;
        let secondarySortB = null;

        if (isJersey) {
          setA = parseJerseySortValue(a.player?.[sortBy]);
          setB = parseJerseySortValue(b.player?.[sortBy]);
        }

        if (isVerified || isName) {
          setA = String(a.player?.[sortBy]);
          setB = String(b.player?.[sortBy]);
        }

        if (isClassRank) {
          const classA = a.player?.classRank;
          const classB = b.player?.classRank;
          const classRankIndexA = getClassRank(classA);
          const classRankIndexB = getClassRank(classB);

          const classRankValueA = classRankValue[classRankIndexA];
          const classRankValueB = classRankValue[classRankIndexB];

          setA = classRankValueA;
          setB = classRankValueB;
        }

        if (isAge) {
          setA = a?.player?.age;
          setB = b?.player?.age;
        }

        if (isPosition) {
          setA = a?.player?.position?.name;
          setB = b?.player?.position?.name;
        }

        if (isRating) {
          setA = a?.rosterRating?.rating;
          setB = b?.rosterRating?.rating;
          secondarySortA = a.rating?.consensusRating;
          secondarySortB = b.rating?.consensusRating;
        }

        if (isNilValue) {
          setA = a?.nilValue?.totalValue || 0;
          setB = b?.nilValue?.totalValue || 0;
        }

        // secondary rating sort by HS rating if no college rating exists
        if (!setA && !setB && (secondarySortA || secondarySortB)) {
          setA = secondarySortA;
          setB = secondarySortB;
        }

        //  puts nulls at the end of list
        if (!setA && setA !== 0) {
          return 1;
        }

        if (!setB && setB !== 0) {
          return -1;
        }

        if (setA < setB) {
          return ascending ? 1 : -1;
        }

        if (setA > setB) {
          return ascending ? -1 : 1;
        }

        return 0;
      });

      return sortedList;
    },
    [list],
  );

export const TeamRosterList = ({
  rosterList,
  rosterHeader,
  title,
  years,
  teams,
}: ITeamRosterProps) => {
  const { hasRdbAccess } = useAuth();
  const router = useRouter();
  const { year, sport } = router.query;
  const { headCoach } = rosterHeader || {};
  const sortList = useSortList(rosterList?.list);
  const [selected, setSelected] = useState('jerseyNumber');
  const [ascending, setAscending] = useState(false);
  const [list, setList] = useState(sortList(selected, ascending) || []);
  const [toggle, setToggle] = useToggle(false);

  const formattedAverageNilValue =
    rosterHeader?.averageNilValue &&
    formatMillions(rosterHeader.averageNilValue);
  const formattedtotalNilValue =
    rosterHeader?.totalNilValue && formatMillions(rosterHeader.totalNilValue);

  const handleToggle = useCallback(
    () => setToggle(!toggle),
    [setToggle, toggle],
  );
  const handleChange: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      const { dataset } = e.currentTarget;
      const { sorttype = '', flip = 'false' } = dataset;
      //  flips order of the list
      const isFlipped = flip === 'true';
      let orderBy = ascending;

      if (sorttype !== selected) {
        setSelected(sorttype);
        orderBy = isFlipped;
        setAscending(isFlipped);
      } else {
        orderBy = !ascending;
        setAscending((prev) => !prev);
      }

      const sortedList = sortList(sorttype, orderBy);

      setList(sortedList || []);
    },
    [ascending, selected, sortList],
  );

  const org = list?.[0]?.organization;

  return (
    <section className={styles.block}>
      {hasRdbAccess() && org && (
        <a
          href={`${databaseUrl}/organization/${org?.slug}-${org?.key}/roster/?sport=${sport}&year=${year}`}
          rel="noopener noreferrer"
          target="_blank"
        >
          Edit
        </a>
      )}
      <header className={styles.header}>
        <h1>{title}</h1>

        <div className={styles.filterWrapper}>
          <InterestListFilters teams={teams} years={years} />

          <Toggle
            checked={toggle}
            id="industry-comparison-toggle"
            label="Industry Comparison"
            onChange={handleToggle}
          />
        </div>
      </header>
      <div className={styles.container}>
        <InterestsNav />
        {headCoach?.fullName && (
          <div className={styles.coachContainer}>
            <dl>
              <dt>Head Coach</dt>
              <dd>
                <Link
                  href={createUrl(['rivals', 'coach', headCoach.slug])}
                  useSiteColor
                >
                  {headCoach?.fullName}
                </Link>
              </dd>
            </dl>
            {headCoach?.orgSeasonCount && (
              <dl>
                <dt>Season</dt>
                <dd>{ordinalSuffixOf(headCoach?.orgSeasonCount)}</dd>
              </dl>
            )}

            {!!formattedAverageNilValue && (
              <dl>
                <dt>Avg. NIL</dt>
                <dd>{`$${formattedAverageNilValue}`}</dd>
              </dl>
            )}
            {!!formattedtotalNilValue && (
              <dl>
                <dt>Total NIL</dt>
                <dd>{`$${formattedtotalNilValue}`}</dd>
              </dl>
            )}
          </div>
        )}
      </div>

      {toggle ? (
        <IndustryComparisonListAlt
          columns={[
            'jerseyNumber',
            'lastName',
            'Industry',
            'On3',
            '247',
            'Espn',
            'Rivals',
            'status',
          ]}
          playerList={list}
        />
      ) : (
        <div className={styles.listWrapper}>
          {list?.length ? (
            <>
              <TeamRosterListHeader
                ascending={ascending}
                onChange={handleChange}
                selected={selected}
              />
              <ul className={styles.teamRosterListContainer}>
                {list.map((item, index) => (
                  <TeamRosterItem
                    {...item}
                    key={`${item.psoKey}-${index}-${ascending}-${selected}-roster`}
                  />
                ))}
              </ul>
            </>
          ) : (
            <p className={styles.noListText}>
              There are no players for this selection.
            </p>
          )}
        </div>
      )}
    </section>
  );
};
